import { useAddPromocodes } from 'features/add-promocodes';
import { useGetCompanyGroups } from 'features/get-company-groups';
import { useGetCompanyRoles } from 'features/get-company-roles';
import { useMemo, useState } from 'react';
import {
  DateInput,
  Form,
  NumberInput,
  required,
  SaveButton,
  SelectInput,
  useShowContext,
  useRefresh,
  useNotify,
  minValue,
  maxValue,
} from 'react-admin';
import { LABELS } from 'shared/constants/labels';
import { VARS } from 'shared/constants/vars';
import styled from 'styled-components';

const promocodeExpireDateDefaultValue = new Date(
  new Date().getFullYear(),
  new Date().getMonth() + 6,
).toISOString();

interface AddPromocodesFormProps {
  onSuccess?: () => void;
}

export const AddPromocodesForm = ({ onSuccess }: AddPromocodesFormProps) => {
  const { record } = useShowContext();
  const [promocodeType, setPromocodeType] = useState<string>(VARS.promocodeTypes[0].id);
  const refresh = useRefresh();
  const notify = useNotify();

  const { addPromocodes, isLoading: isLoadingAddPromocodes } = useAddPromocodes({
    companyId: record.id,
  });
  const { roles, isLoading: isLoadingRoles } = useGetCompanyRoles({
    companyId: record.id,
  });
  const { groups, isLoading: isLoadingGroups } = useGetCompanyGroups({
    companyId: record.id,
  });

  const [rolesChoices, groupsChoices] = useMemo(() => {
    const rolesChoices = roles.map(role => ({ id: role, name: role }));
    const groupsChoices = groups.map(group => ({ id: group, name: group }));

    return [rolesChoices, groupsChoices];
  }, [roles, groups]);

  if (record.isArchive) {
    return <Title>Вы не можете добавить промокоды архивированной компании</Title>;
  }

  const handleSubmit = async (payload: any) => {
    const promocode = payload;

    if (!promocode) {
      notify('Промокоды не могут быть пустыми', { type: 'error' });
      return;
    }

    const expireDate = new Date(promocode.promocodeExpireDate);
    expireDate.setHours(15);

    await addPromocodes({
      promocode: {
        quantity: promocode.quantity,
        amount: promocode.amount || undefined,
        percentage: promocode.percentage || undefined,
        expireDate: expireDate.toISOString(),
        useLimitPerUser: promocode.useLimitPerUser,
      },
      company: {
        id: record.id,
        role: !!promocode.role ? promocode.role : undefined,
        group: !!promocode.group ? promocode.group : undefined,
      },
    });

    notify('Промокоды успешно добавлены', { type: 'success' });
    onSuccess?.();
    refresh();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <SelectInput
        isLoading={isLoadingRoles}
        disabled={!roles || roles.length === 0}
        label={LABELS.companyRole}
        source="role"
        fullWidth
        choices={rolesChoices}
      />
      <SelectInput
        isLoading={isLoadingGroups}
        disabled={!groups || groups.length === 0}
        label={LABELS.companyGroup}
        source="group"
        fullWidth
        choices={groupsChoices}
      />
      <SelectInput
        value={promocodeType}
        defaultValue={promocodeType}
        onChange={(event) => setPromocodeType(event.target.value)}
        label={LABELS.promocodeType}
        source="type"
        validate={[required()]}
        fullWidth
        choices={VARS.promocodeTypes}
      />
      {promocodeType === 'amount' && (
        <NumberInput
          label={LABELS.promocodeAmount}
          source="amount"
          fullWidth
          validate={[
            required(),
            minValue(100, 'Минимальная сумма промокода - 100'),
            maxValue(100000, 'Максимальная сумма промокода - 100 000'),
          ]}
        />
      )}
      {promocodeType === 'percent' && (
        <NumberInput
          label={LABELS.promocodePercentage}
          source="percentage"
          fullWidth
          validate={[
            required(),
            minValue(1, 'Минимальный процент скидки - 5%'),
            maxValue(100, 'Максимальный процент скидки - 100%'),
          ]}
        />
      )}
      <NumberInput
        label={LABELS.promocodeQuantity}
        source="quantity"
        fullWidth
        validate={[
          required(),
          minValue(1, 'Минимальный значение 1'),
          maxValue(10, 'Максимальный значение 10'),
        ]}
      />
      <NumberInput
        label={LABELS.promocodeLimitPerUser}
        source="useLimitPerUser"
        defaultValue={1}
        fullWidth
        validate={[
          required(),
          minValue(1, 'Минимальное значение 1'),
          maxValue(50, 'Максимальное значение 50'),
        ]}
      />
      <DateInput
        label={LABELS.promocodeExpireDate}
        source="promocodeExpireDate"
        defaultValue={promocodeExpireDateDefaultValue}
        validate={[
          required(),
          (value) => {
            if (!value) return undefined;

            const date = new Date(value);
            const now = new Date();
            now.setHours(0, 0, 0, 0);

            return date < now ? 'Дата должна быть в будущем' : undefined;
          },
        ]}
        fullWidth
      />
      <SaveButton disabled={isLoadingAddPromocodes} />
    </Form>
  );
};

const Title = styled.h2`
  text-align: center;
  font-family: sans-serif;
  color: #494848;
`;
